import { createFormHook, formOptions } from "@tanstack/react-form";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { fieldContext, formContext } from "./Form";
import { IconSelect, Input } from "./FormComponents";
import {
    useCreateShortcut,
    useUpdateShortcut,
} from "@dashboard/features/Dashboard/Shortcuts/useShortcuts";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import type { CreateShortcutInput, TaskShortcut, UpdateShortcutInput } from "@dashboard/api/task";

export const TITLE_MAX_LENGTH = 20;

export type ShortcutFormValues = {
    title: string;
    url: string;
    icon: string;
};

export function isValidUrl(value: string): boolean {
    try {
        const url = new URL(value.trim());
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

const { useAppForm: useShortcutForm } = createFormHook({
    fieldContext,
    formContext,
    fieldComponents: { Input, IconSelect },
    formComponents: {},
});

export const useShortcutFormSetup = () => {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const createMutation = useCreateShortcut();
    const updateMutation = useUpdateShortcut();

    const formSchema = z.object({
        title: z
            .string()
            .trim()
            .min(1, t("dashboard.shortcuts.titleRequired"))
            .max(
                TITLE_MAX_LENGTH,
                t("dashboard.shortcuts.titleTooLong", { max: TITLE_MAX_LENGTH }),
            ),
        url: z
            .string()
            .trim()
            .min(1, t("dashboard.shortcuts.urlRequired"))
            .max(2000, t("dashboard.shortcuts.urlTooLong", { max: 2000 }))
            .refine(isValidUrl, t("dashboard.shortcuts.urlInvalid")),
        icon: z.string(),
    });

    const mapShortcutToForm = (shortcut: TaskShortcut): ShortcutFormValues => ({
        title: shortcut.title,
        url: shortcut.url,
        icon: shortcut.icon,
    });

    const handleShortcutSubmit = async (value: ShortcutFormValues, shortcutId?: number) => {
        try {
            const payload = {
                title: value.title.trim(),
                url: value.url.trim(),
                icon: value.icon,
            };
            if (shortcutId) {
                await updateMutation.mutateAsync({
                    id: shortcutId,
                    data: payload satisfies UpdateShortcutInput,
                });
            } else {
                await createMutation.mutateAsync({
                    instanceId,
                    ...payload,
                } satisfies CreateShortcutInput);
            }
            return { success: true as const };
        } catch (error) {
            console.error("Failed to save shortcut:", error);
            return { success: false as const, error };
        }
    };

    const formOpts = formOptions({
        defaultValues: {
            title: "",
            url: "",
            icon: "globe",
        } as ShortcutFormValues,
        validators: {
            onSubmit: formSchema,
        },
    });

    return {
        useShortcutForm,
        formOpts,
        mapShortcutToForm,
        handleShortcutSubmit,
    };
};
